const knownCompound = [
	'test.ts',
	'test.js',
	'test.tsx',
	'test.jsx',
	'spec.ts',
	'spec.js',
	'd.ts',
	'config.js',
	'config.ts',
];

// Dotfiles and extension-less files resolve by basename, not extension.
// `.zshrc` has no real extension, so it must be matched before getExt().
const knownDotfileIcons: Record<string, string> = {
	// zsh / bash shells
	'.zshrc': 'catppuccin:bash',
	'.zshenv': 'catppuccin:bash',
	'.zprofile': 'catppuccin:bash',
	'.zlogin': 'catppuccin:bash',
	'.zlogout': 'catppuccin:bash',
	'.bashrc': 'catppuccin:bash',
	'.bash_profile': 'catppuccin:bash',
	'.bash_login': 'catppuccin:bash',
	'.bash_logout': 'catppuccin:bash',
	'.bash_aliases': 'catppuccin:bash',
	'.profile': 'catppuccin:bash',
	// git
	'.gitconfig': 'lucide:git-branch',
	'.gitattributes': 'lucide:git-branch',
	'.gitmodules': 'lucide:git-branch',
	'.gitignore': 'lucide:git-branch',
	'.gitignore_global': 'lucide:git-branch',
	gitignore: 'lucide:git-branch',
	'gitconfig.toml': 'lucide:git-branch',
	// package / app config without an extension
	'.npmrc': 'catppuccin:npm',
	npmrc: 'catppuccin:npm',
	'.taskrc': 'catppuccin:config',
	config: 'catppuccin:config',
};

function getExt(filename: string): string {
	const lower = filename.toLowerCase();
	const compound = knownCompound.find((c) => lower.endsWith(`.${c}`));
	if (compound) return compound;
	const dotIndex = lower.lastIndexOf('.');
	return dotIndex !== -1 ? lower.slice(dotIndex + 1) : '';
}

function getBasename(filename: string): string {
	return filename.split('/').pop()?.toLowerCase() ?? '';
}

export function getFileIcon({
	language,
	filename,
}: {
	language: string;
	filename: string;
}): string {
	const base = filename ? getBasename(filename) : '';
	if (base) {
		const dotfileIcon = knownDotfileIcons[base];
		if (dotfileIcon) return dotfileIcon;
	}

	const ext = filename ? getExt(filename) || language : (language ?? '');

	switch (ext) {
		// @icon
		// --- Web ---
		case 'json':
		case 'jsonc':
		case 'json5':
			return 'catppuccin:json';
		case 'css':
			return 'catppuccin:css';
		case 'scss':
			return 'catppuccin:sass';
		case 'sass':
			return 'catppuccin:sass';
		case 'less':
			return 'catppuccin:less';
		case 'html':
			return 'catppuccin:html';
		case 'xml':
			return 'catppuccin:xml';
		case 'svg':
			return 'catppuccin:svg';
		case 'graphql':
		case 'gql':
			return 'catppuccin:graphql';

		// --- JavaScript / TypeScript ---
		case 'tsx':
		case 'typescriptreact':
			return 'catppuccin:typescript-react';
		case 'jsx':
		case 'javascriptreact':
			return 'catppuccin:javascript-react';
		case 'ts':
		case 'typescript':
		case 'd.ts':
			return 'catppuccin:typescript';
		case 'js':
		case 'javascript':
		case 'mjs':
		case 'cjs':
			return 'catppuccin:javascript';

		// --- Config / Data ---
		case 'yaml':
		case 'yml':
			return 'catppuccin:yaml';
		case 'toml':
			return 'catppuccin:toml';
		case 'ini':
			return 'catppuccin:config';
		case 'env':
			return 'catppuccin:env';
		case 'csv':
			return 'catppuccin:csv';
		case 'sql':
			return 'catppuccin:database';

		// --- Docs ---
		case 'md':
		case 'mdx':
			return 'catppuccin:markdown-mdx';
		case 'txt':
			return 'catppuccin:text';
		case 'pdf':
			return 'catppuccin:pdf';
		case 'tex':
			return 'catppuccin:latex';

		// --- Systems / Low-level ---
		case 'rs':
		case 'rust':
			return 'catppuccin:rust-alt';
		case 'c':
			return 'catppuccin:c';
		case 'h':
			return 'catppuccin:c-header';
		case 'cpp':
		case 'cc':
		case 'cxx':
			return 'catppuccin:cpp';
		case 'hpp':
			return 'catppuccin:cpp-header';
		case 'cs':
			return 'catppuccin:csharp';
		case 'zig':
			return 'catppuccin:zig';
		case 'odin':
			return 'catppuccin:odin';

		// --- JVM ---
		case 'java':
			return 'catppuccin:java';
		case 'kt':
		case 'kts':
			return 'catppuccin:kotlin';
		case 'scala':
			return 'catppuccin:scala';
		case 'groovy':
			return 'catppuccin:groovy';
		case 'gradle':
			return 'catppuccin:gradle';

		// --- Scripting ---
		case 'py':
			return 'catppuccin:python';
		case 'rb':
			return 'catppuccin:ruby';
		case 'php':
			return 'catppuccin:php';
		case 'lua':
			return 'catppuccin:lua';
		case 'pl':
		case 'pm':
			return 'catppuccin:perl';
		case 'r':
			return 'catppuccin:r';
		case 'bash':
		case 'sh':
		case 'zsh':
			return 'catppuccin:bash';
		case 'fish':
			return 'catppuccin:bash';
		case 'ps1':
		case 'psm1':
			return 'catppuccin:powershell';
		case 'bat':
		case 'cmd':
			return 'logos:microsoft-windows-icon';

		// --- Functional ---
		case 'lisp':
			return 'catppuccin:lisp';
		case 'clj':
		case 'cljs':
		case 'cljc':
			return 'catppuccin:clojure';
		case 'ex':
		case 'exs':
			return 'catppuccin:elixir';
		case 'erl':
		case 'hrl':
			return 'catppuccin:erlang';
		case 'hs':
			return 'catppuccin:haskell';
		case 'ml':
		case 'mli':
			return 'catppuccin:ocaml';
		case 'fs':
		case 'fsx':
			return 'catppuccin:fsharp';
		case 'elm':
			return 'catppuccin:elm';

		// --- Go / Dart / Swift / Others ---
		case 'go':
			return 'catppuccin:go';
		case 'dart':
			return 'catppuccin:dart';
		case 'swift':
			return 'catppuccin:swift';
		case 'vue':
			return 'catppuccin:vue';
		case 'svelte':
			return 'catppuccin:svelte';
		case 'astro':
			return 'catppuccin:astro';
		case 'nim':
			return 'catppuccin:nim';
		case 'cr':
			return 'catppuccin:crystal';
		case 'jl':
			return 'catppuccin:julia';

		// --- DevOps / Infra ---
		case 'dockerfile':
			return 'catppuccin:docker';
		case 'tf':
		case 'tfvars':
			return 'catppuccin:terraform';
		case 'nix':
			return 'catppuccin:nix';
		case 'proto':
			return 'catppuccin:proto';

		// --- Images / Media ---
		case 'png':
		case 'jpg':
		case 'jpeg':
		case 'gif':
		case 'webp':
		case 'avif':
			return 'catppuccin:image';
		case 'mp4':
		case 'mov':
		case 'webm':
			return 'catppuccin:video';
		case 'mp3':
		case 'wav':
		case 'ogg':
			return 'catppuccin:audio';

		// --- Misc ---
		case 'wasm':
			return 'catppuccin:rust';
		case 'conf':
			return 'catppuccin:config';
		case 'lock':
			return 'catppuccin:lock';
		case 'zip':
		case 'tar':
		case 'gz':
		case '7z':
			return 'catppuccin:zip';
		case 'log':
			return 'catppuccin:log';

		// --- Templates ---
		case 'tera':
			return 'vscode-icons:file-type-tera';

		default:
			return 'catppuccin:file';
	}
}
